import { Auth0Client } from '@auth0/auth0-spa-js'

import router from '@/router'
import { AUTH_CONFIG } from './auth0-variables'

const REDIRECT_URI_CACHE_KEY = 'redirect_uri'

/**
 * Shared Auth0 SPA client instance.
 *
 * Uses Refresh Token Rotation instead of the legacy silent-auth iframe
 * approach.  Tokens are cached in localStorage so they survive page
 * reloads; the refresh token is rotated on every use so a leaked token
 * is invalidated as soon as the legitimate session refreshes.
 */
const auth0 = new Auth0Client({
  domain: AUTH_CONFIG.domain,
  clientId: AUTH_CONFIG.clientId,
  authorizationParams: {
    audience: AUTH_CONFIG.audience,
    redirect_uri: window.location.origin,
    scope: 'openid',
  },
  useRefreshTokens: AUTH_CONFIG.useRefreshTokens,
  cacheLocation: AUTH_CONFIG.cacheLocation,
})

/**
 * Return a valid access token, refreshing silently if needed.
 *
 * This is the primary entry-point for request.ts (and any other code
 * that needs the Bearer token).  The SDK handles caching internally —
 * it will return a cached token when it is still valid and
 * transparently use the refresh token to obtain a new one when the
 * access token has expired.
 */
export async function getAccessToken (): Promise<string> {
  try {
    return await auth0.getTokenSilently()
  } catch (err: any) {
    // If the refresh token has been revoked or the session is truly
    // gone, force a fresh login rather than leaving the user in limbo.
    if (err?.error === 'login_required' || err?.error === 'missing_refresh_token') {
      login()
    }
    throw err
  }
}

export function login () {
  const redirectUri = window.location.hash.split('#')[1]
  localStorage.setItem(REDIRECT_URI_CACHE_KEY, redirectUri)
  auth0.loginWithRedirect({
    authorizationParams: {
      redirect_uri: window.location.origin,
    },
  })
}

export function logout () {
  // Clear app-specific data (filters, cached user, etc.).
  // The SDK's own cache (@@auth0spajs@@ keys) is also cleared since
  // we're ending the session entirely — auth0.logout() will redirect
  // the user to Auth0's logout endpoint and back to our /logout page.
  localStorage.clear()

  auth0.logout({
    logoutParams: {
      returnTo: `${window.location.origin}/logout`,
    },
  })
}

/**
 * Called on every app boot (main.ts).
 *
 * 1. If the URL contains an authorization code (post-redirect from
 *    Auth0), exchange it for tokens and restore the pre-login route.
 * 2. Otherwise check if we already have a valid session (cached tokens
 *    in localStorage).
 * 3. If neither, trigger a new login.
 */
export async function handleAuthentication (): Promise<void> {
  const params = new URLSearchParams(window.location.search)

  // Coming back from Auth0 with an authorization code
  if (params.has('code') && params.has('state')) {
    try {
      await auth0.handleRedirectCallback()

      // Clean the code/state params from the URL so a page refresh
      // doesn't try to re-exchange them.
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash)

      // Restore the route the user was on before the redirect
      const redirectUri = localStorage.getItem(REDIRECT_URI_CACHE_KEY)
      if (redirectUri) {
        router.push(redirectUri)
        localStorage.removeItem(REDIRECT_URI_CACHE_KEY)
      }
    } catch (err) {
      console.error('Auth0 redirect callback error:', err)
      alert(`Authentication error. Check the console for details.`)
      throw err
    }
    return
  }

  // No redirect callback — check for an existing session
  const isAuthed = await auth0.isAuthenticated()
  if (!isAuthed) {
    login()
    // Return a never-resolving promise so the main.ts .then() chain
    // doesn't proceed before the redirect happens.
    return new Promise(() => {})
  }
}
